import React from 'react';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CampaignIcon from '@mui/icons-material/Campaign';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SchoolIcon from '@mui/icons-material/School';
import SecurityIcon from '@mui/icons-material/Security';
import { Box, Chip, Divider, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { accountDisplayName, formatRoleLabel } from '@/auth/accountIdentity';
import { accountStateOf, NOT_ACTIVATED_LABEL } from '@/auth/accountState';
import DemoViewSwitch from '@/components/common/DemoViewSwitch';
import SandboxViewSwitch, {
  useSandboxEntry,
} from '@/components/common/SandboxViewSwitch';
import { ROLES } from '@/config/roles.js';
import { useAuth } from '@/hooks/useAuth';

const UserInfoContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
}));

const getRoleChipProps = (roleValue: string | undefined) => {
  switch (roleValue) {
    case ROLES.SUPER_ADMIN:
      return { color: 'secondary' as const, icon: <SecurityIcon /> };
    case ROLES.ADMIN:
      return { color: 'primary' as const, icon: <AdminPanelSettingsIcon /> };
    case ROLES.TEACHER:
      return { color: 'success' as const, icon: <SchoolIcon /> };
    case ROLES.AFFILIATE:
      return { color: 'warning' as const, icon: <CampaignIcon /> };
    case ROLES.ACADEMIC_DIRECTOR:
      return { color: 'primary' as const, icon: <AssignmentTurnedInIcon /> };
    case ROLES.USER:
    default:
      return { color: 'info' as const, icon: <PersonOutlineIcon /> };
  }
};

interface UserInfoProps {
  showDivider?: boolean;
}

const UserInfo = ({ showDivider = true }: UserInfoProps) => {
  const {
    user,
    isAuthenticated,
    userRole,
    isRoleLoaded,
    userStatus,
    authError,
  } = useAuth();
  const sandboxEntry = useSandboxEntry();

  if (!isAuthenticated || !user) {
    return null;
  }

  // Same rule as the header: the role chip waits for the server to confirm
  // the role, and an account awaiting activation says so instead.
  const accountState = accountStateOf({
    isRoleLoaded,
    userStatus,
    authError: authError ?? null,
  });

  return (
    <>
      <UserInfoContainer>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            textAlign: 'center',
          }}
        >
          {accountDisplayName(user)}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            mt: 0.5,
            fontSize: '0.875rem',
          }}
        >
          {user.email}
        </Typography>
        {accountState === 'active' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.25 }}>
            <Chip
              size="small"
              variant={userRole === ROLES.AFFILIATE ? 'filled' : 'outlined'}
              label={formatRoleLabel(userRole)}
              {...getRoleChipProps(userRole)}
              sx={{
                fontWeight: 600,
                letterSpacing: 0.2,
                px: 0.5,
                '& .MuiChip-icon': { fontSize: 18 },
              }}
            />
          </Box>
        )}
        {accountState === 'inactive' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.25 }}>
            <Chip
              size="small"
              variant="outlined"
              label={NOT_ACTIVATED_LABEL}
              icon={<PersonOutlineIcon />}
              sx={{
                fontWeight: 600,
                letterSpacing: 0.2,
                px: 0.5,
                '& .MuiChip-icon': { fontSize: 18 },
              }}
            />
          </Box>
        )}
        {sandboxEntry ? (
          // The panel's rows, bled to the panel's 8px inset.
          <>
            <Divider sx={{ mx: -2, mt: 2, mb: 1 }} />
            <Box sx={{ mx: -1, mb: -1 }}>
              <SandboxViewSwitch />
            </Box>
          </>
        ) : null}
        <DemoViewSwitch layout="drawer" size="small" />
      </UserInfoContainer>
      {showDivider && <Divider />}
    </>
  );
};

export default UserInfo;
